import * as THREE from "three/webgpu";
import type { GroupTransform } from "./storeTypes.ts";

export interface EulerTransform {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
}

const _scaledLocal = new THREE.Vector3();
const _worldPos = new THREE.Vector3();
const _parentQuat = new THREE.Quaternion();
const _localQuat = new THREE.Quaternion();
const _worldQuat = new THREE.Quaternion();
const _parentInvQuat = new THREE.Quaternion();
const _euler = new THREE.Euler();

export function cloneGroupTransform(transform: GroupTransform): GroupTransform {
  return {
    position: [...transform.position],
    rotation: [...transform.rotation],
    scale: [...transform.scale],
  };
}

export function isIdentityGroupTransform(transform: GroupTransform): boolean {
  const p = transform.position;
  const r = transform.rotation;
  const s = transform.scale;
  return (
    p[0] === 0 &&
    p[1] === 0 &&
    p[2] === 0 &&
    r[0] === 0 &&
    r[1] === 0 &&
    r[2] === 0 &&
    r[3] === 1 &&
    s[0] === 1 &&
    s[1] === 1 &&
    s[2] === 1
  );
}

export function cloneEulerTransform(transform: EulerTransform): EulerTransform {
  return {
    position: [...transform.position],
    rotation: [...transform.rotation],
    scale: [...transform.scale],
  };
}

export function composeGroupTransform(
  parent: GroupTransform,
  local: EulerTransform,
): EulerTransform {
  _scaledLocal.set(
    parent.scale[0] * local.position[0],
    parent.scale[1] * local.position[1],
    parent.scale[2] * local.position[2],
  );
  _parentQuat.set(
    parent.rotation[0],
    parent.rotation[1],
    parent.rotation[2],
    parent.rotation[3],
  );
  _worldPos.copy(_scaledLocal).applyQuaternion(_parentQuat);
  _worldPos.x += parent.position[0];
  _worldPos.y += parent.position[1];
  _worldPos.z += parent.position[2];

  _localQuat.setFromEuler(
    _euler.set(local.rotation[0], local.rotation[1], local.rotation[2]),
  );
  _worldQuat.copy(_parentQuat).multiply(_localQuat);
  _euler.setFromQuaternion(_worldQuat);

  return {
    position: [_worldPos.x, _worldPos.y, _worldPos.z],
    rotation: [_euler.x, _euler.y, _euler.z],
    scale: [
      parent.scale[0] * local.scale[0],
      parent.scale[1] * local.scale[1],
      parent.scale[2] * local.scale[2],
    ],
  };
}

export function decomposeWorldToLocalTransform(
  parent: GroupTransform,
  world: EulerTransform,
): EulerTransform {
  _parentQuat.set(
    parent.rotation[0],
    parent.rotation[1],
    parent.rotation[2],
    parent.rotation[3],
  );
  _parentInvQuat.copy(_parentQuat).invert();

  _worldPos.set(
    world.position[0] - parent.position[0],
    world.position[1] - parent.position[1],
    world.position[2] - parent.position[2],
  );
  _worldPos.applyQuaternion(_parentInvQuat);

  const localPosition: [number, number, number] = [
    parent.scale[0] !== 0 ? _worldPos.x / parent.scale[0] : _worldPos.x,
    parent.scale[1] !== 0 ? _worldPos.y / parent.scale[1] : _worldPos.y,
    parent.scale[2] !== 0 ? _worldPos.z / parent.scale[2] : _worldPos.z,
  ];

  _worldQuat.setFromEuler(
    _euler.set(world.rotation[0], world.rotation[1], world.rotation[2]),
  );
  _localQuat.copy(_parentInvQuat).multiply(_worldQuat);
  _euler.setFromQuaternion(_localQuat);

  return {
    position: localPosition,
    rotation: [_euler.x, _euler.y, _euler.z],
    scale: [
      parent.scale[0] !== 0 ? world.scale[0] / parent.scale[0] : world.scale[0],
      parent.scale[1] !== 0 ? world.scale[1] / parent.scale[1] : world.scale[1],
      parent.scale[2] !== 0 ? world.scale[2] / parent.scale[2] : world.scale[2],
    ],
  };
}

export function groupTransformFromEulerTransform(
  transform: EulerTransform,
): GroupTransform {
  _worldQuat.setFromEuler(
    _euler.set(
      transform.rotation[0],
      transform.rotation[1],
      transform.rotation[2],
    ),
  );
  return {
    position: [...transform.position],
    rotation: [_worldQuat.x, _worldQuat.y, _worldQuat.z, _worldQuat.w],
    scale: [...transform.scale],
  };
}

export function eulerFromGroupTransform(
  transform: GroupTransform,
): EulerTransform {
  _worldQuat.set(
    transform.rotation[0],
    transform.rotation[1],
    transform.rotation[2],
    transform.rotation[3],
  );
  _euler.setFromQuaternion(_worldQuat);
  return {
    position: [...transform.position],
    rotation: [_euler.x, _euler.y, _euler.z],
    scale: [...transform.scale],
  };
}
